'use client';

import TalkToBriModalTrigger from '@/components/voice/TalkToBriModalTrigger';

/**
 * The navbar "Talk to Bri" chip — a client island so `StaticLandingNavbar`
 * (a Server Component) keeps its static-render benefits; only this chip
 * hydrates. Shared verbatim between `StaticLandingNavbar` and the hydrated
 * `Navbar`, and between the desktop bar and the mobile menu panel, so the
 * chip always matches the approved mockup's `.nav__voice` spec:
 * cyan-glow text, translucent cyan bg/border, full pill radius, a blipping
 * dot, and a 40px tap target.
 */
export function TalkToBriNavChip({
  fullWidth = false,
  className = '',
  onOpen,
}: {
  fullWidth?: boolean;
  className?: string;
  /** Fired when the chip opens the modal — e.g. the hydrated Navbar's mobile
   *  Sheet instance uses this to close itself, since Radix's own Dialog
   *  (the modal) stacking on top of an already-open Sheet leaves the Sheet's
   *  focus trap/overlay behind it instead of dismissing it. */
  onOpen?: () => void;
}) {
  return (
    <>
      <style>{`
        @keyframes tbb-nav-chip-blip { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        .tbb-nav-chip__dot { animation: tbb-nav-chip-blip 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .tbb-nav-chip__dot { animation: none; }
        }
      `}</style>
      <TalkToBriModalTrigger source="navbar">
        {(open) => (
          <button
            type="button"
            onClick={() => {
              open();
              onOpen?.();
            }}
            aria-haspopup="dialog"
            data-testid="talk-to-bri-nav-chip"
            className={className}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: fullWidth ? 'center' : undefined,
              width: fullWidth ? '100%' : undefined,
              gap: 7,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--cyan-glow)',
              background: 'rgba(34,211,238,0.08)',
              border: '1px solid rgba(125,250,255,0.32)',
              borderRadius: 999,
              padding: '8px 14px',
              minHeight: 40,
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              className="tbb-nav-chip__dot"
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--cyan)',
                boxShadow: '0 0 10px var(--cyan-glow)',
                flexShrink: 0,
              }}
            />
            Talk to Bri
          </button>
        )}
      </TalkToBriModalTrigger>
    </>
  );
}

export default TalkToBriNavChip;
